import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PolicyConsent() {
  const { user, refreshUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const needsConsent =
    !!user &&
    user.policy_version &&
    user.terms_accepted_version !== user.policy_version;

  if (!needsConsent) return null;

  const accept = async () => {
    if (!checked) return;
    setSaving(true);
    try {
      await api.post("/legal/accept");
      await refreshUser();
    } catch (e) {
      toast.error("Could not record your acceptance. Please try again.");
      setSaving(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        data-testid="policy-consent-modal"
        className="sm:max-w-md z-[100] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-indigo-100 text-indigo-600 mx-auto">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h2 className="font-display text-xl font-bold text-center tracking-tight">
          A quick agreement before you start
        </h2>
        <p className="text-sm text-slate-500 text-center -mt-1">
          We've updated our policies. Please review and accept them to continue using Facio Doc.
        </p>

        <label
          htmlFor="policy-consent-checkbox"
          className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 mt-2 cursor-pointer"
        >
          <Checkbox
            id="policy-consent-checkbox"
            data-testid="policy-consent-checkbox"
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            className="mt-0.5"
          />
          <span className="text-sm text-slate-700 leading-relaxed">
            I have read and agree to the{" "}
            <a
              data-testid="policy-terms-link"
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 font-medium underline underline-offset-2"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              data-testid="policy-privacy-link"
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 font-medium underline underline-offset-2"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>

        <Button
          data-testid="policy-accept-button"
          onClick={accept}
          disabled={!checked || saving}
          className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white mt-1"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Agree &amp; Continue
        </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
